import { patchClient } from "./patchClient";
import type { SetApiResultPromise } from "./types";
import type {
  MessagingConversation,
  MessagingMessage,
} from "@/model/messaging";
import {
  messagingServiceGetConversation,
  messagingServiceListConversations,
  messagingServiceListMessages,
  type MessagingServiceListConversationsResponse,
  type MessagingServiceGetConversationResponse,
  type MessagingServiceListMessagesResponse,
  messagingServiceCreateDebugConversation,
  messagingServiceSendMessage,
  messagingServiceBatchDeleteMessages,
  messagingServiceGetConversationState,
  messagingServiceSetConversationState,
  type deputy_v1_messaging_ConversationStateWithFlowPage,
  messagingServiceUpdateConversation,
  messagingServiceSetConversationDataLabel,
  messagingServiceRemoveConversationDataLabel,
  messagingServiceBatchEditConversationDataLabels,
  messagingServiceVisitorListMessages,
  messagingServiceSendHumanAgentMessage,
  messagingServiceHumanAgentTakeover,
  messagingServiceCompleteConversation,
  messagingServiceSummarizeConversation,
  messagingServiceGetConversationRuntimeStatus,
  messagingServiceBatchSetMessageStatus,
  messagingServiceCreateConversation,
  type MessagingServiceCreateConversationResponse,
  messagingServiceGetConversationBySourceId,
  messagingServiceGetConversationByExternalId,
  messagingServiceGetLatestSuggestion,
  messagingServiceBatchUpsertMessage,
  messagingServiceCreateConversationMetrics,
  messagingServiceSendMessageFeedback,
  messagingServiceSubmitMessageForm,
  messagingServiceGetConversationsByAssignee,
  messagingServiceConversationJoinAccounts,
  messagingServiceConversationLeaveAccounts,
  messagingServiceBatchGetConversationUnreadCount,
  messagingServiceDeleteConversation,
} from "@deputy-ai/data-platform-api/v1/messaging";
import type { OverrideProperties } from "type-fest";

// --- API functions ---

export type GetconversationListResponse = OverrideProperties<
  MessagingServiceListConversationsResponse,
  {
    conversations?: MessagingConversation[];
  }
>;

export const getConversationListApi = patchClient(
  messagingServiceListConversations as SetApiResultPromise<
    typeof messagingServiceListConversations,
    GetconversationListResponse
  >
);

export type GetConversationResponse = OverrideProperties<
  MessagingServiceGetConversationResponse,
  {
    conversation?: MessagingConversation;
  }
>;
export const getConversationApi = patchClient(
  messagingServiceGetConversation as SetApiResultPromise<
    typeof messagingServiceGetConversation,
    GetConversationResponse
  >
);

export const getConversationBySourceIdApi = patchClient(
  messagingServiceGetConversationBySourceId as SetApiResultPromise<
    typeof messagingServiceGetConversationBySourceId,
    GetConversationResponse
  >
);

export const getConversationByExternalIdApi = patchClient(
  messagingServiceGetConversationByExternalId as SetApiResultPromise<
    typeof messagingServiceGetConversationByExternalId,
    GetConversationResponse
  >
);

export type CreateConversationResponse = OverrideProperties<
  MessagingServiceCreateConversationResponse,
  {
    conversation?: MessagingConversation;
  }
>;

export const createConversationApi = patchClient(
  messagingServiceCreateConversation as SetApiResultPromise<
    typeof messagingServiceCreateConversation,
    CreateConversationResponse
  >,
  {
    authed: false,
  }
);

export type GetConversationMessagesResponse = OverrideProperties<
  MessagingServiceListMessagesResponse,
  {
    messages?: MessagingMessage[];
  }
>;
export const getConversationMessagesApi = patchClient(
  messagingServiceListMessages as SetApiResultPromise<
    typeof messagingServiceListMessages,
    GetConversationMessagesResponse
  >
);

export const createDebugConverationApi = patchClient(
  messagingServiceCreateDebugConversation as SetApiResultPromise<
    typeof messagingServiceCreateDebugConversation,
    GetConversationResponse
  >
);

export const deleteConversationApi = patchClient(
  messagingServiceDeleteConversation
);

export const sendMessageApi = patchClient(messagingServiceSendMessage, {
  authed: false,
});

export const batchDeleteMessagesApi = patchClient(
  messagingServiceBatchDeleteMessages
);

export const bulkUpsertMessagesApi = patchClient(
  messagingServiceBatchUpsertMessage
);

export const sendHumanAgentMessageApi = patchClient(
  messagingServiceSendHumanAgentMessage
);

export type ConversationState =
  deputy_v1_messaging_ConversationStateWithFlowPage;

export const getConversationStateApi = patchClient(
  messagingServiceGetConversationState
);

export const updateConversationStateApi = patchClient(
  messagingServiceSetConversationState
);

export const updateConversationApi = patchClient(
  messagingServiceUpdateConversation
);

export const setConversationDataLabelApi = patchClient(
  messagingServiceSetConversationDataLabel
);

export const removeConversationDataLabelApi = patchClient(
  messagingServiceRemoveConversationDataLabel
);

export const batchEditConversationDataLabelsApi = patchClient(
  messagingServiceBatchEditConversationDataLabels
);

export const listVisitorMessagesApi = patchClient(
  messagingServiceVisitorListMessages
);

export const takeOverConversationApi = patchClient(
  messagingServiceHumanAgentTakeover
);

export const completeConversationApi = patchClient(
  messagingServiceCompleteConversation,
  {
    authed: false,
  }
);

export const summarizeConversationApi = patchClient(
  messagingServiceSummarizeConversation
);

export const getConversationRuntimeStatusApi = patchClient(
  messagingServiceGetConversationRuntimeStatus
);

export const batchSetMessageStatusApi = patchClient(
  messagingServiceBatchSetMessageStatus
);

export const getLatestSuggestionApi = patchClient(
  messagingServiceGetLatestSuggestion
);

export const trackConversation = patchClient(
  messagingServiceCreateConversationMetrics
);

export const sendMessageFeedbackApi = patchClient(
  messagingServiceSendMessageFeedback
);

export const submitMessageFormApi = patchClient(
  messagingServiceSubmitMessageForm,
  {
    authed: false,
  }
);

export const getConversationsByAssigneeApi = patchClient(
  messagingServiceGetConversationsByAssignee
);

export const joinConversationApi = patchClient(
  messagingServiceConversationJoinAccounts
);

export const leaveConversationApi = patchClient(
  messagingServiceConversationLeaveAccounts
);

export const batchGetConversationUnreadCountApi = patchClient(
  messagingServiceBatchGetConversationUnreadCount
);
